import { IconChevronRight } from '@tabler/icons-react';
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';

// Lets a routed page inject buttons into the title row (see PageHeaderActions)
// or override the crumb/title with data DashboardLayout can't know statically
// (see PageHeaderOverride) — without threading props through DashboardLayout
// for every route.
const SlotContext = createContext(null);

/** Wraps the title block + routed page content so both share the slot. */
export function PageHeaderProvider({ children }) {
  const [slot, setSlot] = useState(null);
  const [override, setOverride] = useState(null);
  return (
    <SlotContext.Provider value={{ slot, setSlot, override, setOverride }}>{children}</SlotContext.Provider>
  );
}

/**
 * The "Section > Page" breadcrumb — lives in the top Header (topbar), not
 * the content area. `crumb`/`title` are the route's base values (computed by
 * DashboardLayout); a routed page can extend them with extra segments and/or
 * replace the final bold title via `PageHeaderOverride` (e.g. a to-do list's
 * name the layout has no way to know statically). `crumb` is optional — the
 * dashboard root passes none, so it renders as just the bold title (e.g.
 * "Overview") with no redundant leading segment.
 */
export function Breadcrumb({ crumb, title }) {
  const ctx = useContext(SlotContext);
  const overriding = Boolean(ctx?.override);
  const effectiveTitle = overriding ? ctx.override.title : title;
  const extraCrumbs = overriding ? ctx.override.crumbs || [] : [];

  if (!effectiveTitle) return null;

  // Assemble the full chain: section crumb (from the layout) → any override
  // crumbs → the current page title, then collapse CONSECUTIVE duplicate
  // labels. Without this, a page whose sidebar section AND nav item are both
  // named the same (e.g. the "Programs" item inside the "Programs" section)
  // renders "Programs › Programs"; a page that also injects its own section
  // crumb would compound it further. When a duplicate pair has exactly one
  // linkable side we keep the link, and the final segment always stays the
  // bold "current" one.
  const chain = [
    ...(crumb ? [{ label: crumb }] : []),
    ...extraCrumbs.map((seg) => ({ label: seg.label, to: seg.to })),
    { label: effectiveTitle, current: true },
  ];
  const segments = [];
  chain.forEach((seg) => {
    const prev = segments[segments.length - 1];
    const sameAsPrev =
      prev && String(prev.label ?? '').trim().toLowerCase() === String(seg.label ?? '').trim().toLowerCase();
    if (sameAsPrev) {
      segments[segments.length - 1] = {
        label: seg.current ? seg.label : prev.label,
        to: prev.to || seg.to,
        current: prev.current || seg.current,
      };
    } else {
      segments.push({ ...seg });
    }
  });

  return (
    <nav className="flex min-w-0 items-center gap-2 text-[13px] font-medium text-slate">
      {segments.map((seg, i) => {
        const isLast = i === segments.length - 1;
        return (
          <span key={`${seg.label}-${i}`} className="flex min-w-0 shrink items-center gap-2">
            {i > 0 && <IconChevronRight className="h-3.5 w-3.5 shrink-0 text-silver" />}
            {isLast ? (
              <b className="truncate font-semibold text-ink">{seg.label}</b>
            ) : seg.to ? (
              <Link to={seg.to} className="truncate transition-colors hover:text-ink">
                {seg.label}
              </Link>
            ) : (
              <span className="truncate">{seg.label}</span>
            )}
          </span>
        );
      })}
    </nav>
  );
}

/**
 * Page title, with an actions slot on the right. `heroStyle` (dashboard root
 * only) shows an uppercase caption (date/status) below the title, styled
 * distinctly from the plain-sentence subtitle every other page shows below
 * its title. The breadcrumb itself lives in the Header now — see `Breadcrumb`.
 *
 * A routed page can override the title (e.g. a dynamic list name the layout
 * has no way to know) via `PageHeaderOverride` — see that component.
 */
export function PageHeader({ title, subtitle, heroStyle = false }) {
  const ctx = useContext(SlotContext);
  const ref = useRef(null);

  // When an override is active, the base route's subtitle almost certainly
  // doesn't describe the overridden (dynamic) page, so it isn't inherited.
  const overriding = Boolean(ctx?.override);
  const effectiveTitle = overriding ? ctx.override.title : title;
  const effectiveSubtitle = overriding ? ctx.override.subtitle : subtitle;
  // A page whose own content IS the title (e.g. an editable title field) sets
  // this so the breadcrumb (in Header) still shows the full path, but this
  // static <h1> — which would just echo that same text — is skipped entirely
  // instead of duplicating it.
  const hideHeading = overriding && ctx.override.hideHeading;
  const showSlot = Boolean(effectiveTitle) && !hideHeading;

  // Re-runs whenever the slot div mounts/unmounts (not just on true component
  // mount/unmount, since DashboardLayout keeps this instance alive across
  // routes) so the shared slot ref never goes stale pointing at a detached
  // node once `hideHeading` toggles on then off again.
  useEffect(() => {
    if (!showSlot) return undefined;
    ctx?.setSlot(ref.current);
    return () => ctx?.setSlot(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSlot]);

  if (!showSlot) return null;

  return (
    <div className="mb-8 flex flex-wrap items-end justify-between gap-4 lg:mb-5">
      <div className="min-w-0">
        <h1 className="truncate font-serif text-[28px] font-semibold leading-[1.2] tracking-[-0.015em] text-ink sm:text-[34px] lg:text-[42px]">
          {effectiveTitle}
        </h1>
        {heroStyle && effectiveSubtitle && (
          <p className="mt-0.5 truncate text-[11px] font-semibold uppercase tracking-[0.22em] text-slate">
            {effectiveSubtitle}
          </p>
        )}
        {!heroStyle && effectiveSubtitle && <p className="mt-1 text-sm text-slate">{effectiveSubtitle}</p>}
      </div>
      <div ref={ref} className="flex flex-none flex-wrap items-center gap-2" />
    </div>
  );
}

/** Renders `children` into the current page's title-row slot, if mounted. */
export function PageHeaderActions({ children }) {
  const ctx = useContext(SlotContext);
  if (!ctx?.slot) return null;
  return createPortal(children, ctx.slot);
}

/**
 * Lets a routed page extend the shared header with data DashboardLayout has
 * no way to know statically (e.g. a to-do list's name) — the base `crumb`
 * (e.g. "Workspace") is kept, `crumbs` adds segments after it (each linkable
 * via `to`), and `title` replaces the final bold title. So a nested page
 * reads "Workspace > To-dos > Sprint v1.0.0" (with "To-dos" clickable back
 * to its `to`) instead of the generic route title, with no second local
 * header duplicating it below. Renders nothing itself.
 */
export function PageHeaderOverride({ crumbs, title, subtitle, hideHeading = false }) {
  const ctx = useContext(SlotContext);

  useEffect(() => {
    ctx?.setOverride({ crumbs, title, subtitle, hideHeading });
    return () => ctx?.setOverride(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(crumbs), title, subtitle, hideHeading]);

  return null;
}

export default PageHeader;
